import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, BookOpenCheck, CheckCircle2, Download, FileBarChart, Printer, RefreshCcw, ShieldCheck, Stamp } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const statusLabel = { non_iniziato: "Non iniziato", incompleto: "Incompleto", completo: "Completo" };
const statusClass = { non_iniziato: "bg-red-50 text-red-700", incompleto: "bg-amber-50 text-amber-800", completo: "bg-emerald-50 text-emerald-700" };
const signatureRoleLabels = { datore_lavoro: "Datore di lavoro", rspp: "RSPP", medico_competente: "Medico competente", rls: "RLS" };

function CompanyPicker() {
  const [companies, setCompanies] = useState([]);
  useEffect(() => { apiRequest("/companies").then(setCompanies); }, []);
  return (
    <div className="space-y-6" data-testid="report-picker-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Report DVR</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Seleziona azienda</h2>
        <p className="mt-2 text-slate-600">Genera la bozza strutturata del Documento di Valutazione dei Rischi pronta per la revisione e l&apos;esportazione.</p>
      </section>
      <Card className="shadow-none"><CardContent className="space-y-3 p-6">
        {companies.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500">Nessuna azienda disponibile.</p>}
        {companies.map((company) => (
          <Link key={company.id} to={`/aziende/${company.id}/report-dvr`} className="flex justify-between rounded-md border border-slate-200 p-4 transition hover:bg-slate-50" data-testid={`report-company-${company.id}`}>
            <span className="font-semibold text-slate-950">{company.business_name}</span>
            <Badge className="bg-blue-50 text-blue-900">Apri</Badge>
          </Link>
        ))}
      </CardContent></Card>
    </div>
  );
}

export default function ReportDvr() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [signForm, setSignForm] = useState({ role: "datore_lavoro", signer_name: "", notes: "" });
  const canSign = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP", "MEDICO_COMPETENTE", "RLS"].includes(user.role);
  const canApprove = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN"].includes(user.role);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try { setReport(await apiRequest(`/companies/${id}/dvr-report`)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (!id) return <CompanyPicker />;
  if (!report) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="report-loading">Caricamento report DVR...</div>;

  const assessment = report.assessment || {};
  const isSigned = assessment.status === "signed";
  const signatures = assessment.signatures || [];
  const signedRoles = new Set(signatures.map((s) => s.role));

  const exportHtml = async () => {
    setError("");
    try { await downloadFile(`/companies/${id}/dvr-report/html`, `DVR_${report.company.business_name.replace(/\s+/g, "_")}.html`); }
    catch (err) { setError(err.message); }
  };

  const exportPdf = async () => {
    setError("");
    try { await downloadFile(`/companies/${id}/dvr-report/pdf`, `DVR_${report.company.business_name.replace(/\s+/g, "_")}.pdf`); }
    catch (err) { setError(err.message); }
  };

  const printReport = () => {
    const w = window.open(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${id}/dvr-report/html`, "_blank");
    if (w) setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 1200);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    try {
      await apiRequest(`/companies/${id}/dvr-assessments/${assessment.id}/signatures`, { method: "POST", body: JSON.stringify(signForm) });
      setSignForm({ role: signForm.role, signer_name: "", notes: "" });
      setInfo(`Firma "${signatureRoleLabels[signForm.role]}" registrata`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const approveDvr = async () => {
    setError(""); setInfo("");
    if (!window.confirm("Confermi l'approvazione finale? Verrà generato uno snapshot HTML immutabile e l'assessment sarà firmato (signed).")) return;
    try {
      const result = await apiRequest(`/companies/${id}/dvr-assessments/${assessment.id}/approve`, { method: "POST" });
      setInfo(`DVR firmato. SHA-256: ${result.signed_html_sha256?.slice(0, 16)}... · Snapshot archiviato`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const rs = report.risk_summary || {};

  return (
    <div className="space-y-6" data-testid="report-dvr-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="report-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="report-refresh-button"><RefreshCcw className="h-4 w-4" /> Rigenera</Button>
          <Button variant="outline" onClick={printReport} data-testid="report-print-button"><Printer className="h-4 w-4" /> Stampa</Button>
          <Button variant="outline" onClick={exportHtml} data-testid="report-download-button"><Download className="h-4 w-4" /> Scarica HTML</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={exportPdf} data-testid="report-download-pdf-button"><Download className="h-4 w-4" /> Scarica PDF</Button>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="report-header">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Bozza DVR</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="report-company-name">{report.company.business_name}</h2>
        <p className="mt-2 text-slate-600" data-testid="report-meta">ATECO {report.company.ateco_code || "—"} · P.IVA {report.company.vat_number || "—"} · Generato il {new Date(report.generated_at).toLocaleString("it-IT")}</p>
        <p className="mt-1 text-sm text-slate-500" data-testid="report-legal-reference">Riferimento normativo: {report.legal_reference_version}</p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 flex justify-between text-sm"><span>Completamento generale</span><strong data-testid="report-overall-pct">{report.overall_completion}%</strong></div>
            <Progress value={report.overall_completion} className="h-3" data-testid="report-overall-progress" />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-sm" data-testid="report-risk-summary">
            <div className="rounded-md bg-emerald-50 p-2"><p className="text-xs text-emerald-700">Basso</p><p className="font-bold text-emerald-900" data-testid="risk-summary-basso">{rs.basso || 0}</p></div>
            <div className="rounded-md bg-amber-50 p-2"><p className="text-xs text-amber-700">Moderato</p><p className="font-bold text-amber-900" data-testid="risk-summary-moderato">{rs.moderato || 0}</p></div>
            <div className="rounded-md bg-orange-50 p-2"><p className="text-xs text-orange-700">Signif.</p><p className="font-bold text-orange-900" data-testid="risk-summary-significativo">{rs.significativo || 0}</p></div>
            <div className="rounded-md bg-red-50 p-2"><p className="text-xs text-red-700">Alto</p><p className="font-bold text-red-900" data-testid="risk-summary-alto">{rs.alto || 0}</p></div>
          </div>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="report-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {info && <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="report-info"><CheckCircle2 className="h-4 w-4" /><AlertTitle>Operazione completata</AlertTitle><AlertDescription>{info}</AlertDescription></Alert>}

      <Card className="shadow-none" data-testid="report-signing-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-900" /> Firme e approvazione</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="report-status-row">
            <Badge className={isSigned ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="report-assessment-status">Stato: {isSigned ? "Firmato (signed)" : assessment.status || "—"}</Badge>
            <Badge className="bg-slate-100 text-slate-700" data-testid="report-assessment-version">Versione {assessment.version_number || 1}</Badge>
            {isSigned && assessment.signed_html_sha256 && <Badge className="bg-blue-50 text-blue-900" data-testid="report-sha256">SHA-256 {assessment.signed_html_sha256.slice(0, 12)}…</Badge>}
            {isSigned && assessment.signed_at && <Badge className="bg-slate-100 text-slate-700">Firmato il {new Date(assessment.signed_at).toLocaleString("it-IT")}</Badge>}
          </div>

          <div className="grid gap-2 md:grid-cols-4" data-testid="report-signatures-grid">
            {Object.entries(signatureRoleLabels).map(([key, label]) => {
              const sig = signatures.find((s) => s.role === key);
              return (
                <div key={key} className={`rounded-md border p-3 ${sig ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`signature-slot-${key}`}>
                  <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
                  {sig ? (
                    <div>
                      <p className="font-semibold text-emerald-900" data-testid={`signature-name-${key}`}>{sig.signer_name}</p>
                      <p className="text-xs text-emerald-800">{new Date(sig.signed_at).toLocaleString("it-IT")}</p>
                      {sig.notes && <p className="mt-1 text-xs text-slate-600">{sig.notes}</p>}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">Non firmato</p>
                  )}
                </div>
              );
            })}
          </div>

          {!isSigned && canSign && (
            <form onSubmit={submitSignature} className="grid gap-3 rounded-md bg-slate-50 p-4 md:grid-cols-4" data-testid="signature-form">
              <div className="space-y-2"><Label>Ruolo firmatario</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={signForm.role} onChange={(e) => setSignForm({ ...signForm, role: e.target.value })} data-testid="signature-role-select">{Object.entries(signatureRoleLabels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div className="space-y-2 md:col-span-2"><Label>Nome e cognome</Label><Input value={signForm.signer_name} onChange={(e) => setSignForm({ ...signForm, signer_name: e.target.value })} required minLength={2} placeholder="Nome firmatario" data-testid="signature-name-input" /></div>
              <div className="space-y-2"><Label>Note (opzionale)</Label><Input value={signForm.notes} onChange={(e) => setSignForm({ ...signForm, notes: e.target.value })} placeholder="Es. con prescrizioni" data-testid="signature-notes-input" /></div>
              <Button className="bg-blue-900 hover:bg-blue-800 md:col-span-4" data-testid="signature-submit-button"><Stamp className="h-4 w-4" /> Apponi firma</Button>
            </form>
          )}

          {!isSigned && canApprove && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4" data-testid="approval-block">
              <p className="text-sm text-blue-900">L&apos;approvazione finale genera uno snapshot HTML immutabile (SHA-256) archiviato come documento. Richiede completamento ≥ 70% e firme di Datore di lavoro + RSPP.</p>
              <Button className="mt-3 bg-emerald-700 hover:bg-emerald-800" onClick={approveDvr} data-testid="approve-dvr-button"><CheckCircle2 className="h-4 w-4" /> Approva e firma DVR</Button>
            </div>
          )}

          {isSigned && assessment.snapshot_document_id && (
            <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="signed-snapshot-alert">
              <ShieldCheck className="h-4 w-4" />
              <AlertTitle>DVR firmato e archiviato</AlertTitle>
              <AlertDescription>Snapshot immutabile disponibile nel modulo Documenti (categoria DVR). SHA-256: {assessment.signed_html_sha256}</AlertDescription>
            </Alert>
          )}

          {canApprove && (
            <EidasSigningPanel companyId={id} assessmentId={assessment.id} eidasSignatures={assessment.eidas_signatures || []} onSigned={load} />
          )}
        </CardContent>
      </Card>

      {report.warnings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-900" data-testid="report-warnings">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Punti di attenzione</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 list-inside list-disc space-y-1" data-testid="report-warnings-list">
              {report.warnings.map((w, idx) => <li key={idx} data-testid={`report-warning-${idx}`}>{w}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4" data-testid="report-sections">
        {report.sections.map((section) => (
          <Card key={section.key} className="shadow-none" data-testid={`report-section-${section.key}`}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-heading flex items-center gap-2"><FileBarChart className="h-5 w-5 text-blue-900" /> {section.title}</CardTitle>
                <div className="flex items-center gap-3">
                  <Badge className={statusClass[section.status] || "bg-slate-100 text-slate-700"} data-testid={`section-status-${section.key}`}>{statusLabel[section.status] || section.status}</Badge>
                  <span className="text-sm text-slate-500" data-testid={`section-completion-${section.key}`}>{section.completion}%</span>
                </div>
              </div>
              {section.summary && <p className="mt-2 text-sm text-slate-600" data-testid={`section-summary-${section.key}`}>{section.summary}</p>}
            </CardHeader>
            <CardContent className="space-y-2">
              {section.items.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500" data-testid={`section-empty-${section.key}`}>Nessun elemento registrato.</p>}
              {section.items.slice(0, 12).map((item, idx) => (
                <div key={idx} className="flex flex-col gap-1 rounded-md border border-slate-100 p-3 text-sm sm:flex-row sm:justify-between" data-testid={`section-item-${section.key}-${idx}`}>
                  <span className="font-semibold text-slate-800">{item.label}</span>
                  <span className="text-slate-600">{item.value}</span>
                </div>
              ))}
              {section.items.length > 12 && <p className="text-xs text-slate-500" data-testid={`section-overflow-${section.key}`}>… e altri {section.items.length - 12} elementi nel report esportato.</p>}
              {section.notes && section.notes.map((note, idx) => <p key={idx} className="rounded-md bg-amber-50 p-2 text-sm text-amber-900" data-testid={`section-note-${section.key}-${idx}`}><BookOpenCheck className="mr-2 inline h-4 w-4" />{note}</p>)}
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-slate-500" data-testid="report-disclaimer">Bozza generata automaticamente da DVR ProSuite. Documento non firmato — da revisionare a cura del datore di lavoro, RSPP, medico competente e RLS prima dell&apos;adozione formale ai sensi del D.Lgs. 81/2008.</p>
    </div>
  );
}


function EidasSigningPanel({ companyId, assessmentId, eidasSignatures, onSigned }) {
  const { t: tr } = useTranslation();
  const [prep, setPrep] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerCf, setSignerCf] = useState("");
  const [qtsp, setQtsp] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const prepare = async () => {
    setError(""); setInfo(""); setBusy(true);
    try {
      const res = await apiRequest(`/companies/${companyId}/dvr-assessments/${assessmentId}/eidas/prepare`, { method: "POST" });
      setPrep(res);
      setInfo(`Pacchetto preparato. SHA-256: ${res.manifest.pdf_sha256.slice(0, 16)}…`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const downloadUnsigned = async () => {
    if (!prep) return;
    try { await downloadFile(`/companies/${companyId}/documents/${prep.package_id}/download`, prep.manifest.file_name); }
    catch (err) { setError(err.message); }
  };

  const finalize = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (!file || !signerName) { setError("Caricare il PDF firmato e indicare il nome del firmatario"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("signed_file", file);
      fd.append("signer_name", signerName);
      fd.append("signer_fiscal_code", signerCf);
      fd.append("qtsp_provider", qtsp);
      await apiRequest(`/companies/${companyId}/dvr-assessments/${assessmentId}/eidas/finalize`, { method: "POST", body: fd });
      setInfo(`DVR firmato eIDAS archiviato. Firmatario: ${signerName}`);
      setPrep(null); setFile(null); setSignerName(""); setSignerCf(""); setQtsp("");
      onSigned && onSigned();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-4" data-testid="eidas-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">eIDAS</p>
          <h3 className="mt-1 font-heading text-lg font-bold text-slate-900">{tr("eidas.title")}</h3>
          <p className="mt-1 text-sm text-slate-600">{tr("eidas.subtitle")}</p>
        </div>
        <Badge className="bg-indigo-100 text-indigo-900">Reg. UE 910/2014</Badge>
      </div>

      {eidasSignatures.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="eidas-signed-list">
          {eidasSignatures.map((s, idx) => (
            <div key={idx} className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid={`eidas-signed-${idx}`}>
              <p className="font-semibold text-emerald-900">✓ Firmato eIDAS: {s.signer_name} {s.qtsp_provider && `· ${s.qtsp_provider}`}</p>
              <p className="mt-1 font-mono text-xs text-emerald-700">SHA-256: {s.signed_sha256?.slice(0, 32)}… · {s.signed_at?.slice(0, 19).replace("T", " ")}</p>
            </div>
          ))}
        </div>
      )}

      {error && <Alert variant="destructive" className="mt-3" data-testid="eidas-error"><AlertDescription>{error}</AlertDescription></Alert>}
      {info && <Alert className="mt-3 border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="eidas-info"><AlertDescription>{info}</AlertDescription></Alert>}

      {!prep ? (
        <Button className="mt-4 bg-indigo-700 hover:bg-indigo-800" onClick={prepare} disabled={busy} data-testid="eidas-prepare-button">
          <ShieldCheck className="h-4 w-4" /> {tr("eidas.prepare")}
        </Button>
      ) : (
        <div className="mt-4 space-y-3" data-testid="eidas-finalize-area">
          <div className="rounded-md bg-white border border-indigo-200 p-3 text-xs">
            <p className="font-semibold text-slate-800">{prep.manifest.file_name}</p>
            <p className="mt-1 font-mono text-slate-500">SHA-256: {prep.manifest.pdf_sha256}</p>
            <p className="mt-2 text-slate-600">{tr("eidas.instructions")}</p>
          </div>
          <Button variant="outline" onClick={downloadUnsigned} data-testid="eidas-download-button"><Download className="h-4 w-4" /> {tr("eidas.download_unsigned")}</Button>
          <form onSubmit={finalize} className="grid gap-2 md:grid-cols-3" data-testid="eidas-finalize-form">
            <Input placeholder={tr("eidas.signer_name")} value={signerName} onChange={(e) => setSignerName(e.target.value)} required data-testid="eidas-signer-name" />
            <Input placeholder={tr("eidas.signer_cf")} value={signerCf} onChange={(e) => setSignerCf(e.target.value)} data-testid="eidas-signer-cf" />
            <Input placeholder={tr("eidas.qtsp")} value={qtsp} onChange={(e) => setQtsp(e.target.value)} data-testid="eidas-qtsp" />
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="md:col-span-3 rounded-md border border-indigo-200 bg-white p-2 text-sm" data-testid="eidas-file-input" />
            <Button type="submit" disabled={busy} className="bg-indigo-700 hover:bg-indigo-800 md:col-span-3" data-testid="eidas-finalize-button">
              <Stamp className="h-4 w-4" /> {tr("eidas.finalize")}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
